import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play } from "lucide-react";
import axios from "axios";
import FloatingParticles from "./FloatingParticles";
import HeroDroplets from "./HeroDroplets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAYERS = {
  // REAL Bespoke Brooklyn Bridge hero photo (crew cleaning windows at sunset)
  sky:   "/brand/hero-brooklyn-bridge.webp",
  glass: "/brand/hero-brooklyn-bridge.webp",
};

export default function CinematicHero() {
  const containerRef = useRef();
  const [loaded, setLoaded] = useState(false);
  const [media, setMedia] = useState({ image: LAYERS.glass, video: "" });

  useEffect(() => {
    axios.get(`${API}/site-content`).then((r) => {
      const img = r.data?.hero_image_url;
      const vid = r.data?.hero_video_url || "";
      if (img || vid) setMedia({ image: img || LAYERS.glass, video: vid });
    }).catch(() => {});
    const t = setTimeout(() => setLoaded(true), 400);
    return () => clearTimeout(t);
  }, []);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const smx = useSpring(mx, { stiffness: 40, damping: 22, mass: 1.4 });
  const smy = useSpring(my, { stiffness: 40, damping: 22, mass: 1.4 });
  const bgX = useTransform(smx, [0, 1], [-20, 20]);
  const bgY = useTransform(smy, [0, 1], [-14, 14]);
  const midX = useTransform(smx, [0, 1], [-36, 36]);
  const midY = useTransform(smy, [0, 1], [-22, 22]);
  const titleX = useTransform(smx, [0, 1], [-8, 8]);
  const italicX = useTransform(smx, [0, 1], [-14, 14]);

  const { scrollY } = useScroll();
  const scrollFade = useTransform(scrollY, [0, 800], [1, 0]);
  const scrollScale = useTransform(scrollY, [0, 800], [1, 1.14]);

  useEffect(() => {
    function onMove(e) {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mx.set((e.clientX - rect.left) / rect.width);
      my.set((e.clientY - rect.top) / rect.height);
    }
    const el = containerRef.current;
    el?.addEventListener("mousemove", onMove);
    return () => el?.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <section
      ref={containerRef}
      data-testid="hero-section"
      className="relative w-full overflow-hidden"
      style={{ height: "100vh", minHeight: 720, background: "#020204" }}
    >
      {/* Layer 1 — Sky */}
      <motion.div className="absolute inset-0" style={{ x: bgX, y: bgY, scale: scrollScale }}>
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.08 }}
          transition={{ duration: 26, ease: "linear", repeat: Infinity, repeatType: "reverse" }}
        >
          <img src={LAYERS.sky} alt="" className="w-full h-full object-cover" style={{ filter: "brightness(0.34) contrast(1.15) saturate(0.7)" }} />
        </motion.div>
      </motion.div>

      {/* Layer 2 — Glass architecture */}
      <motion.div className="absolute inset-0" style={{ x: midX, y: midY, opacity: scrollFade }}>
        {media.video ? (
          <video
            src={media.video}
            autoPlay muted loop playsInline
            onLoadedData={() => setLoaded(true)}
            className="w-full h-full object-cover"
            style={{ filter: "brightness(0.45) contrast(1.1) saturate(0.85)" }}
          />
        ) : (
          <motion.img
            src={media.image}
            alt="Cinematic exterior craft"
            onLoad={() => setLoaded(true)}
            className="w-full h-full object-cover"
            initial={{ scale: 1.14 }}
            animate={{ scale: 1.02 }}
            transition={{ duration: 20, ease: [0.16, 1, 0.3, 1] }}
            style={{ filter: "brightness(0.42) contrast(1.15) saturate(0.82)" }}
          />
        )}
      </motion.div>

      {/* Gradients */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "linear-gradient(180deg, rgba(6,6,7,0.55) 0%, rgba(6,6,7,0.28) 40%, rgba(6,6,7,0.7) 75%, rgba(6,6,7,0.98) 100%)",
      }} />
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(1400px 800px at 50% 55%, transparent 20%, rgba(6,6,7,0.78) 95%)",
      }} />
      <div className="absolute inset-x-0 bottom-0 h-96 pointer-events-none" style={{
        background: "radial-gradient(700px 240px at 50% 100%, rgba(198,168,124,0.15), transparent 70%)",
      }} />

      <FloatingParticles count={28} color="rgba(198,168,124,0.55)" />

      {/* Foreground water droplets — closest layer, moves most with mouse */}
      <motion.div className="absolute inset-0 pointer-events-none" style={{ x: useTransform(smx, [0, 1], [-56, 56]), y: useTransform(smy, [0, 1], [-34, 34]) }}>
        <HeroDroplets count={36} opacity={0.55} />
      </motion.div>

      {/* Grain */}
      <div className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-20" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
      }} />

      {/* Slow light streak */}
      <motion.div
        aria-hidden
        className="absolute inset-y-0 pointer-events-none"
        style={{ width: "36%", background: "linear-gradient(105deg, transparent, rgba(255,255,255,0.06) 45%, rgba(255,255,255,0.13) 50%, transparent 60%)" }}
        initial={{ x: "-60%" }}
        animate={{ x: "260%" }}
        transition={{ duration: 11, ease: "linear", repeat: Infinity, repeatDelay: 4 }}
      />

      {/* Top HUD bar */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : -12 }}
        transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="absolute top-0 left-0 right-0 z-10 px-6 md:px-14 pt-32 md:pt-36 flex justify-between items-start font-mono text-[10px] uppercase tracking-[0.36em]"
        style={{ color: "rgba(245,243,238,0.5)" }}
      >
        <Link
          to="/reel"
          data-testid="hero-reel-link"
          data-cursor="hover"
          data-cursor-label="Watch"
          className="flex items-center gap-3 group cursor-pointer transition-opacity hover:opacity-100"
          style={{ opacity: 0.7 }}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "var(--gold)", boxShadow: "0 0 12px var(--gold)" }} />
          <span className="group-hover:tracking-[0.42em] transition-all duration-500">Reel 001</span>
          <span className="w-0 h-px group-hover:w-10 transition-all duration-500" style={{ background: "var(--gold)" }} />
        </Link>
        <div className="text-right hidden md:block">
          <div style={{ color: "var(--gold)" }}>Veteran Owned · Est. 2013</div>
        </div>
      </motion.div>

      {/* Center — ONE message, ONE action */}
      <div className="absolute inset-0 z-10 flex flex-col justify-center px-6 md:px-14 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 50 }}
          transition={{ duration: 1.4, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-6xl"
        >
          <div className="mb-8 flex items-center gap-4">
            <div className="h-px flex-none w-16" style={{ background: "var(--gold)" }} />
            <div className="font-mono text-[10px] uppercase tracking-[0.44em]" style={{ color: "var(--gold)" }}>
              Bespoke · Exterior · Solutions
            </div>
          </div>

          <motion.h1
            className="font-display leading-[0.9]"
            style={{
              color: "var(--text)",
              fontSize: "clamp(3.5rem, 11vw, 13rem)",
              letterSpacing: "-0.04em",
              fontWeight: 400,
              x: titleX,
              textShadow: "0 4px 40px rgba(0,0,0,0.6)",
            }}
          >
            The
          </motion.h1>
          <motion.h1
            className="font-display-italic leading-[0.9]"
            style={{
              color: "var(--gold-2)",
              fontSize: "clamp(3.5rem, 11vw, 13rem)",
              letterSpacing: "-0.035em",
              x: italicX,
              textShadow: "0 4px 60px rgba(198,168,124,0.28), 0 0 32px rgba(198,168,124,0.2)",
            }}
          >
            Standard.
          </motion.h1>
        </motion.div>

        {/* Sub-copy + single CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 30 }}
          transition={{ duration: 1.4, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 max-w-2xl pointer-events-auto"
        >
          <p className="font-body text-lg md:text-2xl leading-relaxed" style={{ color: "var(--text-2)", fontWeight: 300 }}>
            Commercial exterior care for the finest properties in
            <span style={{ color: "var(--gold-2)" }}> NYC</span> &
            <span style={{ color: "var(--gold-2)" }}> North Carolina.</span>
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link
              to="/pricing"
              data-testid="hero-primary-cta"
              data-cursor="hover"
              data-cursor-label="Instant"
              className="btn-glow"
            >
              See Instant Plans <ArrowUpRight size={13} />
            </Link>
            <Link
              to="/audit"
              data-testid="hero-secondary-cta"
              data-cursor="hover"
              className="inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.36em] transition-colors duration-500"
              style={{ color: "var(--text-2)" }}
            >
              <span className="inline-flex items-center justify-center w-10 h-10" style={{ borderRadius: "var(--r-full)", border: "1px solid var(--line-gold)" }}>
                <Play size={11} style={{ color: "var(--gold)" }} fill="var(--gold)" />
              </span>
              AI Property Audit · 60s
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Bottom-right meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: loaded ? 1 : 0 }}
        transition={{ duration: 1.4, delay: 1.0 }}
        className="absolute bottom-24 right-8 md:right-14 z-10 font-mono text-[10px] uppercase tracking-[0.36em] text-right hidden md:block"
        style={{ color: "var(--text-3)" }}
      >
        <div className="mb-2 flex items-center gap-2 justify-end">
          <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#4ade80" }} />
          <span style={{ color: "var(--text-2)" }}>Live · 24/7 Storm Response</span>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: loaded ? 1 : 0 }}
        transition={{ duration: 1.4, delay: 1.2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3"
      >
        <div className="font-mono text-[9px] uppercase tracking-[0.44em]" style={{ color: "var(--text-3)" }}>
          Scroll
        </div>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-8"
          style={{ background: "linear-gradient(to bottom, var(--gold), transparent)" }}
        />
      </motion.div>
    </section>
  );
}
